import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';

/**
 * AddFabricationLocationModal - Modal for quickly adding a new fabrication location
 * 
 * Props:
 * - isOpen: Whether the modal is visible
 * - onClose: Callback to close the modal
 * - onLocationAdded: Callback when a location is successfully added (receives the new location)
 * - existingLocations: Array of existing location names (for client-side duplicate check)
 */
const AddFabricationLocationModal = ({
  isOpen,
  onClose,
  onLocationAdded,
  existingLocations = []
}) => {
  const { instance, accounts } = useMsal();
  const [locationName, setLocationName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Small delay to ensure modal is rendered
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setLocationName('');
      setError('');
      setIsSaving(false);
    }
  }, [isOpen]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    const trimmedName = locationName.trim();
    
    if (!trimmedName) {
      setError('Location name is required');
      return;
    }

    // Client-side duplicate check (case-insensitive)
    const isDuplicate = existingLocations.some(
      loc => loc.location_name?.toLowerCase() === trimmedName.toLowerCase()
    );
    
    if (isDuplicate) {
      setError('A location with this name already exists');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        '/fabrication-locations/quick-add',
        { location_name: trimmedName }
      );

      if (response.data.success) {
        onLocationAdded(response.data.data);
        onClose();
      } else {
        setError(response.data.message || response.data.error || 'Failed to create location');
      }
    } catch (err) {
      console.error('Error creating fabrication location:', err);
      const data = err.response?.data;
      setError(data?.message || data?.error || 'Failed to create location');
    } finally {
      setIsSaving(false);
    }
  }, [locationName, existingLocations, onLocationAdded, onClose, instance, accounts]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  if (!isOpen) return null;

  // Use portal to render modal at document.body level
  // This ensures the modal is not clipped by parent overflow:hidden or transform contexts
  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={handleKeyDown}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '24px',
          minWidth: '320px',
          maxWidth: '400px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 600 }}>
          Add Fabrication Location
        </h3>
        
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label
              htmlFor="location-name"
              style={{
                display: 'block',
                marginBottom: '6px',
                fontSize: '14px',
                fontWeight: 500,
                color: '#374151',
              }}
            >
              Location Name
            </label>
            <input
              ref={inputRef}
              id="location-name"
              type="text"
              value={locationName}
              onChange={(e) => {
                setLocationName(e.target.value);
                setError('');
              }}
              placeholder="Enter location name"
              disabled={isSaving}
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: '14px',
                border: error ? '1px solid #dc2626' : '1px solid #d1d5db',
                borderRadius: '6px',
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'border-color 0.2s',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = error ? '#dc2626' : '#3b82f6';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = error ? '#dc2626' : '#d1d5db';
              }}
            />
            {error && (
              <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: '#dc2626' }}>
                {error}
              </p>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              style={{
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: 500,
                color: '#374151',
                backgroundColor: '#f3f4f6',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                opacity: isSaving ? 0.6 : 1,
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving || !locationName.trim()}
              style={{
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: 500,
                color: 'white',
                backgroundColor: '#3b82f6',
                border: 'none',
                borderRadius: '6px',
                cursor: (isSaving || !locationName.trim()) ? 'not-allowed' : 'pointer',
                opacity: (isSaving || !locationName.trim()) ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {isSaving && (
                <div
                  style={{
                    width: '14px',
                    height: '14px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }}
                />
              )}
              {isSaving ? 'Saving...' : 'Add Location'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default AddFabricationLocationModal;

